import { ReactElement, useMemo } from 'react';

import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { PATHS } from '~constants/index';
import { useAppSelector, useUIV2State } from '~hooks/index';
import { selectAuth } from '~store/slices/auth';
import { isAdminUser, isOnlyCreatorOrder } from '~utils/user';

import {
  classes,
  LateralMenuContainer,
  LateralMenuLogoWrapper,
  LateralMenuLogoLink,
  LateralMenuLogoMinial,
  LateralMenuLogoWhite,
  LateralMenuContentWrapper,
  LateralMenuList,
  LateralMenuListDivider,
} from './styles';
import LateralMenuListItem from './ListItem';

import { MAIN_ITEMS, SUB_ITEMS, ADMIN_ITEMS, HELPER_ITEMS } from './items';
import { CustomerFeedbackItem } from './CustomerFeedbackItem';

const LateralMenu = (): ReactElement => {
  const { user } = useAppSelector(selectAuth);
  const { isEnabledUIV2 } = useUIV2State();

  const isAdminCurrentUser = useMemo(() => isAdminUser(user), [user]);
  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  return (
    <LateralMenuContainer>
      <LateralMenuLogoWrapper className={classes.logoWrapper}>
        <LateralMenuLogoLink to={PATHS.HOME.BASE}>
          <LateralMenuLogoMinial
            src={toAbsoluteUrl('/logo_minimal.svg')}
            alt="Routix Logo Minimal"
            className={classes.logoMinial}
          />

          <LateralMenuLogoWhite
            src={toAbsoluteUrl('/logo_bw.png')}
            alt="Routix Logo White"
            className={classes.logoWhite}
          />
        </LateralMenuLogoLink>
      </LateralMenuLogoWrapper>

      <LateralMenuContentWrapper className={classes.contentWrapper}>
        <LateralMenuList>
          {MAIN_ITEMS.map((mainItem) => (
            <LateralMenuListItem
              key={`menu-main-items-${mainItem.data.title}`}
              {...mainItem}
            />
          ))}
        </LateralMenuList>

        <LateralMenuListDivider />

        {!isUserOnlyCreatorOrder && isEnabledUIV2 && (
          <>
            <LateralMenuList>
              {SUB_ITEMS.map((subItem) => (
                <LateralMenuListItem
                  key={`menu-sub-items-${subItem.data.title}`}
                  {...subItem}
                />
              ))}
            </LateralMenuList>

            <LateralMenuListDivider />
          </>
        )}

        {isAdminCurrentUser && (
          <>
            <LateralMenuList>
              {ADMIN_ITEMS.map((adminItem) => (
                <LateralMenuListItem
                  key={`menu-admin-items-${adminItem.data.title}`}
                  {...adminItem}
                />
              ))}
            </LateralMenuList>

            <LateralMenuListDivider />
          </>
        )}

        <LateralMenuList>
          {HELPER_ITEMS.map((helperItem) => (
            <LateralMenuListItem
              key={`menu-helper-items-${helperItem.data.title}`}
              {...helperItem}
            />
          ))}

          <CustomerFeedbackItem />
        </LateralMenuList>
      </LateralMenuContentWrapper>
    </LateralMenuContainer>
  );
};

export default LateralMenu;
